/**
 * Editing one record: hold the edits, write them back, and end the form.
 *
 * Themes, threads and packs are different records, but editing is one job — load, hold the
 * edits, create or update, go back — and it had been written out three times, which is
 * three places for the error handling to drift. This module owns that job; each caller
 * supplies only what genuinely differs, which is its fields and what create and update mean
 * for it.
 *
 * `toList`/`fromList` are here rather than with the vocabularies because they are about the
 * *input*, not the value: a form edits a comma-separated string and the record holds a
 * list, and that conversion belongs where the form's state does.
 */

import { useEffect, useState, type JSX } from 'react'

import { describeFailure } from '@shared/user-errors.js'
import { t } from '../i18n'

import type { EditConflict, Versioned } from './editConflict'
import { ConflictNotice, useConflict } from './editConflict'

/** An editor's whole state: what is being edited, and how to write it back. */
export interface RecordEditor<TFields> {
  fields: TFields
  set: (patch: Partial<TFields>) => void
  failure: string | null
  ready: boolean
  save: (event: React.FormEvent) => Promise<void>
  /** A save refused because somebody else saved first, or `null`. */
  conflict: EditConflict<TFields> | null
  /** Replace the form with the other version. */
  takeTheirs: () => void
  /** Keep the reader's form and aim the next Save at the other version. */
  keepMine: () => void
}

/**
 * Editing one record: load it, hold the edits, write it back, go back.
 *
 * The themes and threads editors were the same forty lines twice — same load-into-state,
 * same create-or-update branch, same try/catch turning a rejection into a message. They are
 * different records but editing is one job, and two copies of it is two places for the
 * error handling to drift. What stays with each caller is the part that genuinely differs:
 * which fields exist, and what create and update mean for that record.
 *
 * A new record starts on `blank` and is ready immediately; an existing one is not ready
 * until it arrives, so the form cannot render empty inputs over a record that is still
 * loading and then save those blanks over the reader's work.
 *
 * An update carries the version the record was loaded at. When somebody else has saved
 * since, the save is refused and `conflict` holds their version for the reader to compare;
 * nothing is retried on their behalf (`editConflict.tsx`).
 */
export function useRecordEditor<TRecord extends Versioned, TFields>({
  id,
  blank,
  read,
  toForm,
  create,
  update,
  onBack,
}: {
  id: number | null
  blank: TFields
  read: (id: number) => Promise<TRecord>
  toForm: (record: TRecord) => TFields
  create: (fields: TFields) => Promise<unknown>
  update: (id: number, fields: TFields, rowVersion: number) => Promise<unknown>
  onBack: () => void
}): RecordEditor<TFields> {
  const [fields, setFields] = useState<TFields>(blank)
  const [rowVersion, setRowVersion] = useState<number | null>(null)
  const conflicts = useConflict(toForm, setFields, setRowVersion)
  const [failure, setFailure] = useState<string | null>(null)
  const [ready, setReady] = useState(id === null)

  useEffect(() => {
    if (id === null) return
    read(id).then(
      (loaded) => {
        setFields(toForm(loaded))
        setRowVersion(loaded.row_version)
        setReady(true)
      },
      (error: unknown) => setFailure(describeFailure(error, t('error.notLoaded'))),
    )
    // `read` is a fresh closure every render; the id identifies the record.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const set = (patch: Partial<TFields>) => setFields((current) => ({ ...current, ...patch }))

  async function save(event: React.FormEvent) {
    event.preventDefault()
    setFailure(null)
    conflicts.clear()
    try {
      if (id === null) await create(fields)
      else if (rowVersion !== null) await update(id, fields, rowVersion)
      onBack()
    } catch (error) {
      if (!conflicts.refused(error)) setFailure(error instanceof Error ? error.message : t('error.generic'))
    }
  }

  const { conflict, takeTheirs, keepMine } = conflicts
  return { fields, set, failure, ready, save, conflict, takeTheirs, keepMine }
}

/** The refused-save notice for an editor built on `useRecordEditor`, or nothing. */
export function RecordConflict<TFields extends object>({
  editor,
  labels,
}: {
  editor: RecordEditor<TFields>
  labels: Partial<Record<keyof TFields, string>>
}): JSX.Element | null {
  if (editor.conflict === null) return null
  return (
    <ConflictNotice
      conflict={editor.conflict}
      mine={editor.fields}
      labels={labels}
      onUseTheirs={editor.takeTheirs}
      onKeepMine={editor.keepMine}
    />
  )
}

/**
 * Save and cancel, the pair every editor form ends with.
 *
 * One component because four forms had written it out separately, and a difference between
 * them would have been a difference nobody chose.
 */
export function FormActions({
  onCancel,
  busy = false,
}: {
  onCancel: () => void
  busy?: boolean
}): JSX.Element {
  return (
    <div className="form__row">
      <button type="submit" disabled={busy}>
        {t('editor.save')}
      </button>
      <button type="button" className="link" onClick={onCancel}>
        {t('editor.cancel')}
      </button>
    </div>
  )
}

/** Comma-separated text to a list, and back. The one shape both editors use. */
export const toList = (text: string): string[] =>
  text
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

export const fromList = (items: string[] | undefined): string => (items ?? []).join(', ')
